/* Theme behaviour. Kept dependency-free and progressive:
   every feature here degrades to a working page without JS. */

(function () {
  'use strict';

  // Set by initColorRecolor() when a product supports live recoloring, so
  // initGallery() can re-apply it after a thumbnail swaps the main photo.
  var mainMediaRecolorHook = null;

  // Tracks whether the currently-displayed main photo is the one recolor
  // tuning was measured against (see RECOLOR_PRODUCT_CONFIG below).
  // Swatch clicks call applyRecolor() directly - not through
  // initGallery() - so this flag is the only thing standing between a
  // swatch click and re-recoloring whatever photo happens to be
  // showing. Starts true because the page loads on the recolor source.
  var mainMediaIsRecolorSource = true;

  // Per-product recolor tuning, keyed by product handle. The live
  // recolor feature needs to know where a photo's reflection begins so it
  // can leave that area untouched instead of tinting it along with the
  // real body - but that boundary is a fact about each individual photo's
  // framing (how tall the subject stands in the shot), not something a
  // shared formula can reliably guess. An auto-detection heuristic was
  // tried and broke repeatedly: tuning it against one toy's photo silently
  // shifted the boundary for every other toy already live. Measuring the
  // real boundary once per product (as a fraction of image height, via
  // the row-brightness-drop technique used to build this table) and
  // recording it here means a new toy's entry can never alter an existing
  // one's - each row is independent data, not shared derived logic.
  // reflectionBoundary: fraction of frame height below which the
  // reflection-exclusion lightness floor is enforced. Falls back to
  // DEFAULT_REFLECTION_BOUNDARY for any product not listed here yet - if
  // a new toy's recolor tests show bleed into its reflection, or its own
  // legs/tail not recoloring, measure its real boundary and add a row.
  //
  // openingThreshold: how much of a body pixel's 3x3 neighborhood must
  // also be body-classified for it to survive the noise-removal erosion
  // step (see openMask below). The default (0.55) assumes a body region
  // with a smooth, solid color - fine for a single flat print color, but
  // a multi-tone/brindle print's body is legitimately speckled at the
  // pixel level, so that many individual body pixels sit right next to
  // classification failures and get eroded away as if they were noise.
  // Lower this per-product if a toy's own natural color texture (not
  // bleed, not a new bug) is being stripped instead of recolored.
  //
  // saturationThreshold: minimum HSV-style saturation for a pixel to
  // count as "colorful enough to be body" rather than dull/near-white.
  // A flat single-color print's body sits comfortably above the default
  // (0.35) everywhere, but a lighter secondary tone within a multi-tone
  // print (e.g. a washed-out tan patch on an otherwise brown coat) can
  // have real, deliberate color at a genuinely lower saturation than the
  // rest of the body - the same numeric zone as bleed on flat-color
  // photos, but not the same thing here. Lower per-product only when a
  // toy's own lighter natural tone is being left unpainted; a global
  // change here risks reopening the white/near-white bleed this
  // threshold exists to prevent on every other product.
  //
  // chromaFloor: minimum absolute RGB chroma (max-min channel) for a
  // pixel to be considered at all, before the HSV-saturation ratio is
  // even computed - it exists to catch near-black compression noise,
  // where a meaningless few-point channel difference can compute as
  // "100% saturated". How much noise floor a photo actually has varies
  // by photo (some are compressed cleanly, some aren't), and a coat with
  // fine dark shading (deep creases, a jawline crease) can have real body
  // detail at a similarly low absolute chroma. Only lower this for a
  // product after confirming its own background is actually clean at
  // that level (sample the flat background directly - see how
  // snappy-pup's was measured at exactly 0 before lowering this).
  //
  // reflectionLightnessFloor: how dark the reflection-zone lightness
  // floor is (see lightnessFloor below). The default (0.24) assumes the
  // real object's underside goes fully dark quickly once past
  // reflectionBoundary - true for a smaller object sitting higher off
  // the reflective surface, but a low-slung body can have a genuine,
  // gradual underside shadow that's still legitimately part of the
  // object (not reflection yet) in the 0.15-0.24 lightness range right
  // past the boundary. Lower per-product only when the true reflection
  // (further down, well past this transition) is confirmed to sit
  // comfortably below the new floor.
  var DEFAULT_REFLECTION_BOUNDARY = 0.6;
  var DEFAULT_OPENING_THRESHOLD = 0.55;
  var DEFAULT_SATURATION_THRESHOLD = 0.35;
  var DEFAULT_CHROMA_FLOOR = 22;
  var DEFAULT_REFLECTION_LIGHTNESS_FLOOR = 0.24;
  var RECOLOR_PRODUCT_CONFIG = {
    'bite-fin': { reflectionBoundary: 0.66 },
    'rex-clamp': { reflectionBoundary: 0.84 },
    'tiny-fang': { reflectionBoundary: 0.66 },
    'snappy-pup': {
      reflectionBoundary: 0.68,
      openingThreshold: 0.2,
      saturationThreshold: 0.15,
      chromaFloor: 8,
      reflectionLightnessFloor: 0.15
    },
    // Every other product's photo is shot in a real color, so "is this
    // pixel body?" can be answered by hue - does it look like the source
    // color. tri-snap's default is Silver: an achromatic material with
    // no real hue at all (its swatch itself measures ~5% saturation), so
    // hue-matching has nothing to lock onto and classifies almost
    // nothing as body. lightnessBodyMax switches classification to a
    // brightness cutoff instead: anything darker than this is body
    // (paintable), anything at or above it is treated as the white
    // secondary material and left alone. Body-highlight brightness and
    // horn-white brightness genuinely overlap in this photo (confirmed
    // via histogram - no clean gap exists), so the cutoff is deliberately
    // conservative: a few of the body's own brightest highlights staying
    // unpainted is far less noticeable than the white horns picking up a
    // tint.
    'tri-snap': { reflectionBoundary: 0.72, lightnessBodyMax: 0.72 }
  };

  // Real per-color photos beat the live pixel-recolor heuristic outright
  // when they exist - a real photo is never wrong the way a pixel
  // classifier can be. Shopify already gives every variant with an image
  // attached to its variant_ids a "featured_image" field for free (see
  // product.variants | json in the page), so a toy variant just needs
  // that image to be a real hero photo of that color (not the small
  // filament-swatch photo used for the swatch dot) for this to work.
  // Both initVariants() and applyRecolor() call this so neither one has
  // its own copy of "what's currently selected" to go stale.
  function currentVariantFeaturedImageSrc() {
    var dataEl = document.querySelector('[data-variant-data]');
    var optionSelects = document.querySelectorAll('[data-option-select]');
    if (!dataEl || !optionSelects.length) return null;
    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return null;
    }
    var selected = Array.prototype.map.call(optionSelects, function (select) {
      return select.value;
    });
    var variant = variants.find(function (v) {
      return selected.every(function (value, index) {
        return v.options[index] === value;
      });
    });
    if (!variant || !variant.featured_image || !variant.featured_image.src) return null;
    var src = variant.featured_image.src;
    return src.indexOf('//') === 0 ? 'https:' + src : src;
  }

  // Secondary gallery photos (2nd/3rd/4th image, etc.) can't use Shopify's
  // variant-image mechanism the way the hero photo does - a variant only
  // ever points to ONE featured image. Instead, main-product.liquid embeds
  // a per-thumbnail data-color-images JSON map (built from images tagged
  // alt="coloralt:<position>:<Color>") and this pair of helpers resolves
  // it against whatever Primary Color is currently selected, so a gallery
  // photo can swap the same way the hero photo does.
  function currentPrimaryColorValue() {
    // Toys use a "Primary Color" option; mount-style products use a plain
    // "Color" option. Either one drives the per-color gallery photo swap.
    var select = document.querySelector('[data-option-select][data-option-name="Primary Color"]')
      || document.querySelector('[data-option-select][data-option-name="Color"]');
    return select ? select.value : null;
  }

  function colorImageFor(thumb) {
    var raw = thumb.getAttribute('data-color-images');
    if (!raw) return null;
    var color = currentPrimaryColorValue();
    if (!color) return null;
    var map;
    try {
      map = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    return map[color] || null;
  }

  /* Mobile navigation ------------------------------------ */

  function initNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var nav = document.querySelector('[data-nav]');
    if (!toggle || !nav) return;

    toggle.addEventListener('click', function () {
      var open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
    });
  }

  /* Hero slideshow ----------------------------------------- */

  function initHeroSlideshow() {
    var hero = document.querySelector('[data-slideshow]');
    if (!hero) return;

    var slides = hero.querySelectorAll('[data-slide]');
    if (slides.length < 2) return;

    var interval = parseInt(hero.getAttribute('data-interval'), 10) || 6000;
    var index = 0;
    var timer;

    function show(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach(function (slide, si) {
        slide.classList.toggle('is-active', si === index);
      });
    }

    function restart() {
      clearInterval(timer);
      timer = setInterval(function () { show(index + 1); }, interval);
    }

    var prevButton = hero.querySelector('[data-slide-prev]');
    var nextButton = hero.querySelector('[data-slide-next]');

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        show(index - 1);
        restart();
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        show(index + 1);
        restart();
      });
    }

    restart();
  }

  /* Product info tabs ------------------------------------- */

  function initProductTabs() {
    var tabs = document.querySelectorAll('[data-product-tab]');
    if (!tabs.length) return;

    var purchase = document.querySelector('.product__purchase');

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = tab.getAttribute('data-product-tab');

        tabs.forEach(function (t) {
          var active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', String(active));
        });

        document.querySelectorAll('[data-product-tabpanel]').forEach(function (panel) {
          panel.hidden = panel.getAttribute('data-product-tabpanel') !== target;
        });

        // The special-order tab is a request form, not a purchase path -
        // hide the color/size/quantity/add-to-cart box while it's open.
        if (purchase) purchase.hidden = target === 'special-order';
      });
    });
  }

  /* Product gallery -------------------------------------- */

  function initGallery() {
    var main = document.getElementById('ProductMainImage');
    var mainVideo = document.getElementById('ProductMainVideo');
    var mainCanvas = document.getElementById('ProductMainCanvas');
    var thumbs = document.querySelectorAll('[data-thumb]');
    if (!main || !thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var full = thumb.getAttribute('data-full');
        if (!full) return;

        if (thumb.getAttribute('data-type') === 'video' && mainVideo) {
          mainMediaIsRecolorSource = false;
          mainVideo.src = full;
          mainVideo.hidden = false;
          main.hidden = true;
          if (mainCanvas) mainCanvas.hidden = true;
          mainVideo.play();
        } else {
          if (mainVideo) {
            mainVideo.pause();
            mainVideo.hidden = true;
          }

          // If the currently selected color has a real photo of its own
          // (see currentVariantFeaturedImageSrc()), clicking back to the
          // primary thumbnail should show THAT, not fall back to the
          // originally-photographed color's picture - otherwise browsing
          // the gallery while a real-photo color is selected would
          // silently undo the color swap. Secondary thumbnails (2nd/3rd/4th
          // photo) get the same treatment via colorImageFor() - see its
          // definition for why they need their own data-color-images map
          // instead of reusing the variant-image mechanism.
          var realSrc = thumb.hasAttribute('data-recolor-source')
            ? currentVariantFeaturedImageSrc()
            : colorImageFor(thumb);

          main.hidden = false;
          main.src = realSrc || full;
          main.removeAttribute('srcset');

          if (realSrc) {
            mainMediaIsRecolorSource = false;
            if (mainCanvas) mainCanvas.hidden = true;
          } else {
            // Live recoloring is tuned against one specific photo per
            // product (its reflection position, framing, and pose all
            // factor into that tuning - see RECOLOR_PRODUCT_CONFIG). A
            // product's other gallery photos are frequently shot in a
            // completely different pose or crop (a lifestyle shot, a detail
            // close-up), so reapplying the same tuning to them produces
            // exactly the kind of misclassified bleed that cost real time
            // to chase down. Only the thumbnail marked as the recolor
            // source re-triggers it; every other thumbnail shows its
            // original photographed color regardless of the selected
            // swatch. mainMediaIsRecolorSource is also checked directly by
            // applyRecolor() itself, since swatch clicks call it without
            // going through this handler at all.
            mainMediaIsRecolorSource = thumb.hasAttribute('data-recolor-source');
            if (mainMediaIsRecolorSource) {
              if (mainMediaRecolorHook) mainMediaRecolorHook();
            } else if (mainCanvas) {
              mainCanvas.hidden = true;
            }
          }
        }

        thumbs.forEach(function (t) {
          t.classList.remove('is-active');
        });
        thumb.classList.add('is-active');
      });
    });
  }

  /* Color swatches ----------------------------------------- */

  function initProductSwatches() {
    var groups = document.querySelectorAll('[data-swatch-group]');
    if (!groups.length) return;

    groups.forEach(function (group) {
      var optionIndex = group.getAttribute('data-option-index');
      var select = document.querySelector('[data-option-select][data-option-index="' + optionIndex + '"]');
      var nameEl = group.parentElement.querySelector('[data-swatch-name]');
      var preview = group.querySelector('[data-swatch-preview]');
      var previewImg = preview ? preview.querySelector('[data-swatch-preview-img]') : null;
      var swatches = group.querySelectorAll('[data-swatch-value]');

      function positionName(swatch) {
        if (!nameEl) return;
        nameEl.style.left = (swatch.offsetLeft + swatch.offsetWidth / 2) + 'px';
      }

      var initialActive = group.querySelector('.product__swatch.is-active');
      if (initialActive) positionName(initialActive);

      swatches.forEach(function (swatch) {
        swatch.addEventListener('click', function () {
          var value = swatch.getAttribute('data-swatch-value');

          swatches.forEach(function (s) {
            var active = s === swatch;
            s.classList.toggle('is-active', active);
            s.setAttribute('aria-pressed', String(active));
          });

          if (nameEl) {
            nameEl.textContent = value;
            positionName(swatch);
          }

          if (select) {
            select.value = value;
            select.dispatchEvent(new Event('change'));
          }
        });

        swatch.addEventListener('mouseenter', function () {
          var image = swatch.getAttribute('data-swatch-image');
          if (!image || !preview || !previewImg) return;
          previewImg.src = image;
          preview.style.left = (swatch.offsetLeft + swatch.offsetWidth / 2) + 'px';
          preview.hidden = false;
        });

        swatch.addEventListener('mouseleave', function () {
          if (preview) preview.hidden = true;
        });

        swatch.addEventListener('focus', function () {
          swatch.dispatchEvent(new Event('mouseenter'));
        });

        swatch.addEventListener('blur', function () {
          swatch.dispatchEvent(new Event('mouseleave'));
        });
      });
    });
  }

  /* Live photo recoloring -----------------------------------
     For products with "Primary Color" / "Secondary Color" swatch
     options (see the toy pattern), repaint the main product photo so
     its blue body and white accents follow the selected swatches.
     This is a heuristic hue/lightness remap done in a canvas, not a
     true material change - it works well for the clean, evenly-lit
     product photography this pattern expects. */

  function initColorRecolor() {
    var img = document.getElementById('ProductMainImage');
    var canvas = document.getElementById('ProductMainCanvas');
    if (!img || !canvas) return;

    var primarySelect = document.querySelector('[data-option-select][data-option-name="Primary Color"]');
    var secondarySelect = document.querySelector('[data-option-select][data-option-name="Secondary Color"]');
    if (!primarySelect || !secondarySelect) return;

    var primaryGroup = document.querySelector(
      '[data-swatch-group][data-option-index="' + primarySelect.getAttribute('data-option-index') + '"]'
    );
    var secondaryGroup = document.querySelector(
      '[data-swatch-group][data-option-index="' + secondarySelect.getAttribute('data-option-index') + '"]'
    );
    if (!primaryGroup || !secondaryGroup) return;

    var productHandle = document.querySelector('.product__media-main');
    productHandle = productHandle ? productHandle.getAttribute('data-product-handle') : null;
    var recolorConfig = RECOLOR_PRODUCT_CONFIG[productHandle] || {};
    var reflectionBoundary = recolorConfig.reflectionBoundary || DEFAULT_REFLECTION_BOUNDARY;
    var openingThreshold = recolorConfig.openingThreshold || DEFAULT_OPENING_THRESHOLD;
    var saturationThreshold = recolorConfig.saturationThreshold || DEFAULT_SATURATION_THRESHOLD;
    var chromaFloor = recolorConfig.chromaFloor || DEFAULT_CHROMA_FLOOR;
    var reflectionLightnessFloor = recolorConfig.reflectionLightnessFloor || DEFAULT_REFLECTION_LIGHTNESS_FLOOR;
    var lightnessBodyMax = recolorConfig.lightnessBodyMax || null;

    var ctx = canvas.getContext('2d');
    var maxWorkingWidth = 900;

    function rgbToHsl(r, g, b) {
      r /= 255; g /= 255; b /= 255;
      var max = Math.max(r, g, b), min = Math.min(r, g, b);
      var h = 0, s = 0, l = (max + min) / 2;
      if (max !== min) {
        var d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h *= 60;
      }
      return [h, s, l];
    }

    function hslToRgb(h, s, l) {
      h = ((h % 360) + 360) % 360 / 360;
      var r, g, b;
      if (s === 0) {
        r = g = b = l;
      } else {
        var hue2rgb = function (p, q, t) {
          if (t < 0) t += 1;
          if (t > 1) t -= 1;
          if (t < 1 / 6) return p + (q - p) * 6 * t;
          if (t < 1 / 2) return q;
          if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
          return p;
        };
        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
      }
      return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    }

    function hueDistance(a, b) {
      var d = Math.abs(a - b) % 360;
      return d > 180 ? 360 - d : d;
    }

    function swatchRgb(group, valueName) {
      var swatches = group.querySelectorAll('[data-swatch-value]');
      var target = null;
      swatches.forEach(function (s) {
        if (s.getAttribute('data-swatch-value') === valueName) target = s;
      });
      if (!target) return null;
      var color = getComputedStyle(target).backgroundColor;
      var m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (!m) return null;
      return [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)];
    }

    function activeValue(group) {
      var active = group.querySelector('.product__swatch.is-active');
      return active ? active.getAttribute('data-swatch-value') : null;
    }

    // The "source" color is whichever swatch the page renders as active by
    // default - i.e. the color the product photo was actually shot in.
    // Read it from the DOM instead of hardcoding a color name so this
    // works for any product regardless of its photographed color.
    var defaultPrimaryValue = activeValue(primaryGroup);
    var defaultSecondaryValue = activeValue(secondaryGroup);
    var sourcePrimaryHsl = rgbToHsl.apply(null, swatchRgb(primaryGroup, defaultPrimaryValue) || [31, 79, 216]);

    var CLASS_NONE = 0, CLASS_BODY = 1, CLASS_CAST = 2;

    // Secondary Color only ever offers "White", matching the source photo
    // exactly, so there's nothing to repaint there - white/teeth/belly
    // pixels are left completely untouched (not even reclassified), which
    // also sidesteps every edge case that used to cause bleed near the
    // eye, teeth, and body/white boundaries. Only Primary Color body
    // pixels are ever modified.
    function classifyPixels(data, w, h) {
      var mask = new Uint8Array(w * h);

      // HSV-style saturation (chroma / max channel) is scale-invariant
      // under darkening - a dark saturated green and a bright saturated
      // green get the same value - which is what lets genuinely dark body
      // shadow (e.g. inside an open mouth) get classified correctly
      // instead of being excluded the way plain RGB chroma would exclude
      // it. But that same scale-invariance blows up on near-black
      // background/compression noise: a pixel like (8,0,1) is technically
      // "100% saturated" by the ratio even though the 8-point difference
      // is meaningless noise on an essentially black pixel. An absolute
      // chroma floor (skip pixels where max-min is tiny) filters that
      // noise out before the ratio ever gets computed, without punishing
      // real dark color the way a *large* chroma floor would. How much
      // floor a given photo actually needs varies (see chromaFloor in
      // RECOLOR_PRODUCT_CONFIG above).
      var CHROMA_FLOOR = chromaFloor;

      // A reflection on the glossy studio surface reads as a darker,
      // duller copy of the real object - same hue, same hsvSaturation
      // ratio (uniform darkening preserves it), only lower lightness - so
      // color alone can't tell it apart from a genuinely dark shadow on
      // the object itself. Position can: every photo in this style has
      // the object above and its reflection below. WHERE that boundary
      // falls isn't a fixed fraction of the frame though - a tall upright
      // figure's legs can occupy the same rows a shorter figure's
      // reflection would - and auto-detecting it from pixel brightness
      // proved unreliable (a bright tail/fin pixel near the real
      // boundary could push the detected line deep into the actual
      // reflection). reflectionBoundary is instead an explicit per-product
      // constant (see RECOLOR_PRODUCT_CONFIG above), measured once against
      // this photo and never touched again by tuning done for another
      // product.
      var reflectionZoneStart = Math.round(h * reflectionBoundary);

      for (var i = 0, p = 0; i < data.length; i += 4, p++) {
        if (data[i + 3] === 0) continue;
        var hsl = rgbToHsl(data[i], data[i + 1], data[i + 2]);
        var l = hsl[2];
        var y = Math.floor(p / w);
        var lightnessFloor = y > reflectionZoneStart ? reflectionLightnessFloor : 0;

        if (lightnessBodyMax !== null) {
          // Achromatic source material (see lightnessBodyMax in
          // RECOLOR_PRODUCT_CONFIG) - hue and saturation aren't
          // meaningful here, so body is simply "darker than the white
          // secondary material, brighter than the reflection floor".
          if (l < lightnessBodyMax && l > lightnessFloor) {
            mask[p] = CLASS_BODY;
          }
          continue;
        }

        var maxC = Math.max(data[i], data[i + 1], data[i + 2]);
        var minC = Math.min(data[i], data[i + 1], data[i + 2]);
        if (maxC - minC < CHROMA_FLOOR) continue;
        var hsvSaturation = maxC > 0 ? (maxC - minC) / maxC : 0;

        if (hsvSaturation > saturationThreshold && l > lightnessFloor && hueDistance(hsl[0], sourcePrimaryHsl[0]) < 35) {
          mask[p] = CLASS_BODY;
        } else if (y <= reflectionZoneStart && l > 0.4 && hsvSaturation < saturationThreshold && hsvSaturation > 0) {
          // A white/light surface (horn, claw, tooth) right next to the
          // colored body often picks up a faint tint from bounced light
          // in the original photo - too pale to read as "body" (that's
          // correct, it shouldn't get force-painted the target color),
          // but if left completely untouched it stays tinted with the
          // photo's ORIGINAL color, which reads as a visible mismatch
          // once the body around it has changed to a different color.
          // Left alone for the reflection, which is expected to keep
          // looking like an untouched mirror of the original photo.
          mask[p] = CLASS_CAST;
        }
      }
      return mask;
    }

    // Morphological "opening" (erode then dilate) so a small isolated
    // false-positive speck (compression noise, a stray reflection pixel)
    // gets dropped while the real, larger body region survives with its
    // original boundary intact.
    function openMask(mask, w, h, targetClass, threshold) {
      var eroded = new Uint8Array(w * h);
      for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
          var idx = y * w + x;
          if (mask[idx] !== targetClass) continue;
          var match = 0, total = 0;
          for (var dy = -1; dy <= 1; dy++) {
            var ny = y + dy;
            if (ny < 0 || ny >= h) continue;
            for (var dx = -1; dx <= 1; dx++) {
              var nx = x + dx;
              if (nx < 0 || nx >= w) continue;
              total++;
              if (mask[ny * w + nx] === targetClass) match++;
            }
          }
          if (match / total >= threshold) eroded[idx] = 1;
        }
      }

      var opened = new Uint8Array(w * h);
      for (var y2 = 0; y2 < h; y2++) {
        for (var x2 = 0; x2 < w; x2++) {
          var idx2 = y2 * w + x2;
          if (mask[idx2] !== targetClass) continue;
          for (var dy2 = -1; dy2 <= 1 && !opened[idx2]; dy2++) {
            var ny2 = y2 + dy2;
            if (ny2 < 0 || ny2 >= h) continue;
            for (var dx2 = -1; dx2 <= 1; dx2++) {
              var nx2 = x2 + dx2;
              if (nx2 < 0 || nx2 >= w) continue;
              if (eroded[ny2 * w + nx2]) {
                opened[idx2] = 1;
                break;
              }
            }
          }
        }
      }
      return opened;
    }

    function recolorImageData(imageData, targetPrimaryRgb) {
      var data = imageData.data;
      var w = imageData.width, h = imageData.height;
      var targetPrimaryHsl = rgbToHsl(targetPrimaryRgb[0], targetPrimaryRgb[1], targetPrimaryRgb[2]);

      var mask = classifyPixels(data, w, h);
      var bodyKeep = openMask(mask, w, h, CLASS_BODY, openingThreshold);

      for (var i = 0, p = 0; i < data.length; i += 4, p++) {
        if (mask[p] === CLASS_CAST && !bodyKeep[p]) {
          // Neutralize a color-cast highlight by averaging its channels -
          // this removes the tint (chroma) while keeping the pixel's
          // original brightness, so a horn/claw/tooth that had a faint
          // red cast in the source photo reads as clean white regardless
          // of which color the body is currently painted.
          var avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          data[i] = avg;
          data[i + 1] = avg;
          data[i + 2] = avg;
          continue;
        }
        if (!bodyKeep[p]) continue;
        var hsl = rgbToHsl(data[i], data[i + 1], data[i + 2]);
        var nl = hsl[2] * 0.55 + targetPrimaryHsl[2] * 0.45;
        var rgb = hslToRgb(targetPrimaryHsl[0], targetPrimaryHsl[1], nl);
        data[i] = rgb[0];
        data[i + 1] = rgb[1];
        data[i + 2] = rgb[2];
      }
      return imageData;
    }

    var recolorGeneration = 0;

    function applyRecolor() {
      var generation = ++recolorGeneration;

      // Swatch clicks call this directly, regardless of which gallery
      // photo is currently on screen. If that's not the photo recolor
      // tuning was measured against, do nothing - leave the plain
      // original showing rather than misapplying another photo's
      // classification to a composition it was never tuned for.
      if (!mainMediaIsRecolorSource) {
        canvas.hidden = true;
        img.hidden = false;
        return;
      }

      var primaryValue = activeValue(primaryGroup);
      var secondaryValue = activeValue(secondaryGroup);
      if (!primaryValue || !secondaryValue) return;

      // Nothing to do when both swatches are already at the photo's
      // native colors - keep showing the plain, unprocessed photo.
      if (primaryValue === defaultPrimaryValue && secondaryValue === defaultSecondaryValue) {
        canvas.hidden = true;
        img.hidden = false;
        return;
      }

      var targetPrimaryRgb = swatchRgb(primaryGroup, primaryValue);
      var targetSecondaryRgb = swatchRgb(secondaryGroup, secondaryValue);
      if (!targetPrimaryRgb || !targetSecondaryRgb) return;

      var source = new Image();
      source.crossOrigin = 'anonymous';
      source.onload = function () {
        // A newer swatch click started (or reset) while this photo was
        // decoding - drop this stale result instead of overwriting it.
        if (generation !== recolorGeneration) return;

        var scale = Math.min(1, maxWorkingWidth / source.naturalWidth);
        var w = Math.round(source.naturalWidth * scale);
        var h = Math.round(source.naturalHeight * scale);
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(source, 0, 0, w, h);
        try {
          var imageData = ctx.getImageData(0, 0, w, h);
          recolorImageData(imageData, targetPrimaryRgb);
          ctx.putImageData(imageData, 0, 0);
          canvas.hidden = false;
          img.hidden = true;
        } catch (e) {
          // CORS or decode failure - fall back to the plain photo.
          canvas.hidden = true;
          img.hidden = false;
        }
      };
      source.onerror = function () {
        if (generation !== recolorGeneration) return;
        canvas.hidden = true;
        img.hidden = false;
      };
      source.src = img.src;
    }

    primaryGroup.querySelectorAll('[data-swatch-value]').forEach(function (swatch) {
      swatch.addEventListener('click', applyRecolor);
    });
    secondaryGroup.querySelectorAll('[data-swatch-value]').forEach(function (swatch) {
      swatch.addEventListener('click', applyRecolor);
    });

    mainMediaRecolorHook = applyRecolor;
  }

  /* Variant selection ------------------------------------ */

  function initVariants() {
    var dataEl = document.querySelector('[data-variant-data]');
    var variantSelect = document.querySelector('[data-variant-select]');
    var optionSelects = document.querySelectorAll('[data-option-select]');
    if (!dataEl || !variantSelect || !optionSelects.length) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var priceEl = document.querySelector('[data-price]');
    var addButton = document.querySelector('[data-add-button]');
    var qtyInput = document.getElementById('Quantity');
    var currentUnitPrice = null;

    var main = document.getElementById('ProductMainImage');
    var mainVideo = document.getElementById('ProductMainVideo');
    var mainCanvas = document.getElementById('ProductMainCanvas');
    // Captured before anything touches the page, so it's genuinely the
    // photographed default - the thing to fall back to when the
    // selected variant has no real photo of its own.
    var defaultMainSrc = main ? main.src : null;
    var defaultMainSrcset = main ? main.getAttribute('srcset') : null;

    function currentOptions() {
      return Array.prototype.map.call(optionSelects, function (select) {
        return select.value;
      });
    }

    function findVariant(selected) {
      return variants.find(function (variant) {
        return selected.every(function (value, index) {
          return variant.options[index] === value;
        });
      });
    }

    function formatMoney(cents) {
      // Shopify's money formatting varies by store, so fall back to the
      // page's existing currency string shape where possible.
      return (cents / 100).toFixed(2);
    }

    function updatePriceDisplay() {
      if (!priceEl || currentUnitPrice === null) return;
      var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      if (!qty || qty < 1) qty = 1;
      priceEl.textContent = formatMoney(currentUnitPrice * qty);
    }

    function update() {
      var variant = findVariant(currentOptions());

      if (!variant) {
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = addButton.getAttribute('data-unavailable') || 'Unavailable';
        }
        return;
      }

      variantSelect.value = variant.id;

      if (typeof variant.price === 'number') {
        currentUnitPrice = variant.price;
        updatePriceDisplay();
      }

      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }

      // A real photo of this exact variant beats any pixel-recolor
      // heuristic - see currentVariantFeaturedImageSrc(). Setting
      // mainMediaIsRecolorSource here is what keeps applyRecolor() (in
      // initColorRecolor(), triggered by the same swatch click, always
      // runs after this - see the initProductSwatches/initColorRecolor
      // registration order note near mainMediaIsRecolorSource's
      // declaration) from fighting this over which image ends up
      // showing: it already bails out whenever the flag is false.
      if (main) {
        var realSrc = variant.featured_image && variant.featured_image.src
          ? (variant.featured_image.src.indexOf('//') === 0 ? 'https:' + variant.featured_image.src : variant.featured_image.src)
          : null;
        if (realSrc) {
          if (mainVideo) {
            mainVideo.pause();
            mainVideo.hidden = true;
          }
          main.hidden = false;
          main.src = realSrc;
          main.removeAttribute('srcset');
          if (mainCanvas) mainCanvas.hidden = true;
          mainMediaIsRecolorSource = false;
        } else if (main.src !== defaultMainSrc) {
          // Coming back from a real-photo color to one that doesn't have
          // its own photo yet (the originally photographed color, or an
          // older toy not yet migrated) - restore the true default image
          // instead of leaving the last color's photo on screen, and let
          // the live-recolor system take back over for it.
          main.src = defaultMainSrc;
          if (defaultMainSrcset) main.setAttribute('srcset', defaultMainSrcset);
          else main.removeAttribute('srcset');
          mainMediaIsRecolorSource = true;
          if (mainMediaRecolorHook) mainMediaRecolorHook();
        }

        // The hero thumbnail's own small preview image is a separate DOM
        // node from #ProductMainImage, so swapping the main photo above
        // doesn't touch it - without this it was the only one of the 4
        // gallery thumbnails that stayed on the default color's picture.
        var heroThumbImg = document.querySelector('[data-thumb][data-recolor-source] [data-thumb-img]');
        if (heroThumbImg) {
          heroThumbImg.src = realSrc || heroThumbImg.getAttribute('data-default-src');
        }
      }

      // Secondary gallery photos (2nd/3rd/4th image, etc.) get swapped the
      // same way the hero photo is above, via colorImageFor() - see its
      // definition for why they need their own data-color-images map
      // instead of the variant-image mechanism. Runs on every option
      // change (not just color) since it's cheap and a no-op on products
      // with no such thumbnails.
      document.querySelectorAll('[data-thumb][data-color-images]').forEach(function (thumb) {
        var thumbImg = thumb.querySelector('[data-thumb-img]');
        if (!thumbImg) return;
        var colorSrc = colorImageFor(thumb);
        thumbImg.src = colorSrc || thumbImg.getAttribute('data-default-src');

        if (thumb.classList.contains('is-active') && main) {
          if (mainVideo) {
            mainVideo.pause();
            mainVideo.hidden = true;
          }
          main.hidden = false;
          main.src = colorSrc || thumb.getAttribute('data-full');
          main.removeAttribute('srcset');
          if (mainCanvas) mainCanvas.hidden = true;
          mainMediaIsRecolorSource = false;
        }
      });

      // Keep the URL shareable without reloading the page.
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    optionSelects.forEach(function (select) {
      select.addEventListener('change', update);
    });

    if (qtyInput) {
      qtyInput.addEventListener('input', updatePriceDisplay);
    }

    // Only offer colors that actually exist for the selected material,
    // instead of showing every color the product has across all materials.
    var materialSelect = document.querySelector('[data-option-select][data-option-name="Material"]');
    var colorSelect = document.querySelector('[data-option-select][data-option-name="Color"]');
    var swatchGroup = document.querySelector('[data-swatch-group]');
    var materialIndex = materialSelect ? parseInt(materialSelect.getAttribute('data-option-index'), 10) : -1;
    var colorIndex = colorSelect ? parseInt(colorSelect.getAttribute('data-option-index'), 10) : -1;

    function validColorsFor(materialValue) {
      var valid = {};
      variants.forEach(function (variant) {
        if (variant.options[materialIndex] === materialValue) {
          valid[variant.options[colorIndex]] = true;
        }
      });
      return valid;
    }

    function applyColorFilter() {
      var valid = validColorsFor(materialSelect.value);
      var activeSwatchHidden = false;

      if (swatchGroup) {
        var swatches = swatchGroup.querySelectorAll('[data-swatch-value]');
        swatches.forEach(function (swatch) {
          var isValid = !!valid[swatch.getAttribute('data-swatch-value')];
          swatch.hidden = !isValid;
          if (!isValid && swatch.classList.contains('is-active')) activeSwatchHidden = true;
        });
      }

      Array.prototype.forEach.call(colorSelect.options, function (opt) {
        var isValid = !!valid[opt.value];
        opt.hidden = !isValid;
        opt.disabled = !isValid;
      });

      // The color that was selected doesn't exist for the newly chosen
      // material, so fall back to the first color that does.
      if (activeSwatchHidden) {
        var firstValid = swatchGroup.querySelector('[data-swatch-value]:not([hidden])');
        if (firstValid) firstValid.click();
      }
    }

    if (materialSelect && colorSelect && materialIndex > -1 && colorIndex > -1) {
      materialSelect.addEventListener('change', applyColorFilter);
      applyColorFilter();
    }

    // Populate currentUnitPrice from the already-selected variant so the
    // quantity field can multiply against it immediately, without
    // requiring the customer to touch an option dropdown first.
    update();
  }

  /* Footer photo gallery ----------------------------------- */

  function initFooterGallery() {
    var galleries = document.querySelectorAll('[data-gallery]');
    var modal = document.querySelector('[data-gallery-modal]');
    if (!galleries.length || !modal) return;

    var imageEl = modal.querySelector('[data-gallery-image]');
    var dotsEl = modal.querySelector('[data-gallery-dots]');
    var images = [];
    var index = 0;

    function renderDots() {
      if (!dotsEl) return;
      dotsEl.innerHTML = '';
      images.forEach(function (img, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'gallery-modal__dot';
        dot.setAttribute('aria-label', 'Go to photo ' + (i + 1) + ' of ' + images.length);
        dot.addEventListener('click', function () { show(i); });
        dotsEl.appendChild(dot);
      });
    }

    function updateDots() {
      if (!dotsEl) return;
      var dots = dotsEl.querySelectorAll('.gallery-modal__dot');
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    function show(i) {
      if (!images.length) return;
      index = (i + images.length) % images.length;
      imageEl.src = images[index].src;
      imageEl.alt = images[index].alt;
      updateDots();
    }

    function open(imgs) {
      images = imgs;
      modal.removeAttribute('hidden');
      renderDots();
      show(0);
    }

    function close() {
      modal.setAttribute('hidden', '');
      imageEl.src = '';
    }

    modal.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-gallery-close')) close();
    });

    var prevButton = modal.querySelector('[data-gallery-prev]');
    var nextButton = modal.querySelector('[data-gallery-next]');
    if (prevButton) prevButton.addEventListener('click', function () { show(index - 1); });
    if (nextButton) nextButton.addEventListener('click', function () { show(index + 1); });

    document.addEventListener('keydown', function (e) {
      if (modal.hasAttribute('hidden')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    });

    galleries.forEach(function (gallery) {
      var trigger = gallery.querySelector('[data-gallery-open]');
      var links = gallery.querySelectorAll('[data-gallery-img]');
      if (!trigger || !links.length) return;

      var imgs = Array.prototype.map.call(links, function (a) {
        return { src: a.getAttribute('href'), alt: a.textContent.trim() };
      });

      trigger.addEventListener('click', function () {
        open(imgs);
      });

      // Cycle the small cover thumbnail through the uploaded photos so
      // the footer isn't just showing a single static image.
      var coverImg = trigger.querySelector('img');
      var cycleUrls;
      try {
        cycleUrls = JSON.parse(trigger.getAttribute('data-cycle-images') || '[]');
      } catch (e) {
        cycleUrls = [];
      }

      if (coverImg && cycleUrls.length > 1) {
        var coverIndex = 0;
        setInterval(function () {
          coverIndex = (coverIndex + 1) % cycleUrls.length;
          coverImg.src = cycleUrls[coverIndex];
        }, 3500);
      }
    });
  }

  /* Address book delete confirmation ---------------------- */

  function initAddressDelete() {
    var buttons = document.querySelectorAll('[data-confirm]');

    buttons.forEach(function (button) {
      button.addEventListener('click', function (e) {
        if (!window.confirm(button.getAttribute('data-confirm'))) {
          e.preventDefault();
        }
      });
    });
  }

  /* Cart drawer --------------------------------------------- */

  function initCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    var body = drawer.querySelector('[data-cart-drawer-body]');
    var countEls = document.querySelectorAll('[data-cart-count]');

    function open() {
      drawer.removeAttribute('hidden');
    }

    function close() {
      drawer.setAttribute('hidden', '');
    }

    function applyFragment(doc) {
      var freshBody = doc.querySelector('[data-cart-drawer-body]');
      if (freshBody && body) body.innerHTML = freshBody.innerHTML;

      var countValue = doc.querySelector('[data-cart-count-value]');
      if (countValue) {
        countEls.forEach(function (el) { el.textContent = countValue.textContent.trim(); });
      }
    }

    function refresh() {
      return fetch('/?section_id=cart-drawer')
        .then(function (res) { return res.text(); })
        .then(function (html) {
          applyFragment(new DOMParser().parseFromString(html, 'text/html'));
        });
    }

    function changeLine(line, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      }).then(refresh);
    }

    document.querySelectorAll('[data-cart-toggle]').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        open();
        refresh();
      });
    });

    drawer.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-cart-close')) {
        close();
        return;
      }

      var qtyButton = e.target.closest('[data-cart-qty]');
      if (qtyButton) {
        var qtyItem = qtyButton.closest('[data-cart-line]');
        var valueEl = qtyItem.querySelector('[data-cart-qty-value]');
        var qty = parseInt(valueEl.textContent, 10);
        qty = qtyButton.getAttribute('data-cart-qty') === 'increase' ? qty + 1 : qty - 1;
        changeLine(parseInt(qtyItem.getAttribute('data-cart-line'), 10), Math.max(qty, 0));
        return;
      }

      var removeButton = e.target.closest('[data-cart-remove]');
      if (removeButton) {
        var removeItem = removeButton.closest('[data-cart-line]');
        changeLine(parseInt(removeItem.getAttribute('data-cart-line'), 10), 0);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (drawer.hasAttribute('hidden')) return;
      if (e.key === 'Escape') close();
    });

    var productForm = document.getElementById('ProductForm');
    if (productForm) {
      var submitButton = productForm.querySelector('[data-add-button]');

      productForm.addEventListener('submit', function (e) {
        e.preventDefault();
        if (submitButton) submitButton.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(productForm)
        })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (err) { return Promise.reject(err); });
            open();
            return refresh();
          })
          .catch(function () {
            // AJAX add failed (e.g. sold out mid-request) - fall back to a
            // normal form submission so the shopper still reaches the cart.
            productForm.submit();
          })
          .then(function () {
            if (submitButton) submitButton.disabled = false;
          });
      });
    }
  }

  /* Cart hover preview --------------------------------------- */

  function initCartPreview() {
    var wrapper = document.querySelector('[data-cart-hover]');
    var preview = document.querySelector('[data-cart-preview]');
    if (!wrapper || !preview) return;

    var hideTimer;
    var customPrintFallbackImage = wrapper.getAttribute('data-custom-print-fallback-image');

    function money(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    function resizeImage(url, width) {
      if (!url) return '';
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }

    function render(cart) {
      if (!cart.item_count) {
        preview.innerHTML = '<p class="cart-preview__empty">Your cart is empty.</p>';
        return;
      }

      var shown = cart.items.slice(0, 4);
      var itemsHtml = shown.map(function (item) {
        // Only item.image is a genuine Shopify CDN product image - the
        // other two fall back to unmodified URLs, same as the cart
        // drawer's own Liquid rendering of these same fallbacks.
        var thumb = item.image ? resizeImage(item.image, 120) :
          (item.properties && item.properties['Preview image']) ||
          (item.handle === 'custom-3d-print' ? customPrintFallbackImage : null);

        var img = thumb
          ? '<img src="' + thumb + '" alt="" width="60" height="60" loading="lazy">'
          : '';
        return (
          '<li class="cart-preview__item">' +
            img +
            '<span>' +
              '<span class="cart-preview__item-title">' + item.product_title + '</span>' +
              '<span class="cart-preview__item-qty">Qty ' + item.quantity + '</span>' +
            '</span>' +
            '<span>' + money(item.final_line_price) + '</span>' +
          '</li>'
        );
      }).join('');

      var remaining = cart.items.length - shown.length;
      var moreHtml = remaining > 0
        ? '<p class="cart-preview__more">+' + remaining + ' more item' + (remaining === 1 ? '' : 's') + '</p>'
        : '';

      preview.innerHTML =
        '<ul class="cart-preview__items">' + itemsHtml + '</ul>' +
        moreHtml +
        '<p class="cart-preview__subtotal"><span>Subtotal</span><span>' + money(cart.total_price) + '</span></p>' +
        '<div class="cart-preview__actions">' +
          '<a href="/cart" class="btn btn--secondary">View cart</a>' +
          '<a href="/cart/checkout" class="btn btn--primary">Checkout</a>' +
        '</div>';
    }

    function show() {
      clearTimeout(hideTimer);
      preview.removeAttribute('hidden');
      fetch('/cart.js')
        .then(function (res) { return res.json(); })
        .then(render);
    }

    function scheduleHide() {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () {
        preview.setAttribute('hidden', '');
      }, 150);
    }

    wrapper.addEventListener('mouseenter', show);
    wrapper.addEventListener('mouseleave', scheduleHide);
    preview.addEventListener('mouseenter', function () { clearTimeout(hideTimer); });
    preview.addEventListener('mouseleave', scheduleHide);

    var toggle = wrapper.querySelector('[data-cart-toggle]');
    if (toggle) {
      toggle.addEventListener('click', function () {
        preview.setAttribute('hidden', '');
      });
    }
  }

  function init() {
    initNav();
    initHeroSlideshow();
    initGallery();
    initProductTabs();
    initProductSwatches();
    // initColorRecolor() (live canvas pixel-recolor) is disabled as of
    // 2026-08-23 - it required extensive per-photo tuning (reflection
    // boundary, chroma floor, saturation threshold, an entire separate
    // classification mode for achromatic source colors) and kept finding
    // new failure modes on every new toy. The function is left in place,
    // just not invoked, in case it's needed again. Toys now rely
    // entirely on the real per-color photo swap in initVariants() -
    // colors without a real photo attached simply show the default
    // photographed color until one is provided, rather than guessing at
    // a recolor.
    initFooterGallery();
    initVariants();
    initAddressDelete();
    initCartDrawer();
    initCartPreview();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
